import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { useAuth } from '../../contexts/AuthContext';
import { useAPI } from '../../contexts/APIContext';
import { EVENTS } from '../../constants/events';
import { captureAdClicks } from '../../utils/adAttribution';
import { clearLinkFragment, handoffMadeHere, readLinkFragment, restoreHandoff } from '../../utils/emailLinkSignin';
import logger from '../../utils/logger';

/**
 * In front of the funnel page: a visit that arrives with an emailed sign-in link (`#pml=` token) and/or a funnel
 * hand-off (`#fh=` id, from the link or from Instagram's "Open in external browser") is finished here before the
 * funnel mounts, because the funnel reads its saved progress once, on mount.
 *
 *   1. the fragment is cleared from the address bar at once (it signs someone in)
 *   2. the hand-off is loaded into this browser's storage (+ its URL params and ad click)
 *   3. the token is turned into a session (the same handleSuccessfulAuth a Google sign-in ends in)
 *   4. the funnel mounts on the restored step, signed in, and carries on exactly as after Google
 *
 * A link that fails (expired, used, an outage) mounts the funnel with `emailLinkNotice`, which the sign-in step
 * shows above a fresh "email me a link". Anything without a fragment passes straight through.
 */
// Kept in step with the inline script in public/index.html, which shows the same words from the first paint
export const landingText = (link) => (link.token ? 'Signing you in…' : 'Picking up where you left off…');

const EmailLinkGate = ({ children }) => {
  const [link] = useState(() => {
    const found = readLinkFragment();
    // This tab's own hand-off (a reload on the sign-in step): nothing to restore
    return handoffMadeHere(found.handoffId) && !found.token ? { token: null, handoffId: null } : found;
  });
  const active = !!(link.token || link.handoffId);
  const [done, setDone] = useState(!active);
  const [notice, setNotice] = useState(null);
  const { signInWithEmailLink, loading } = useAuth();
  const { apiClient } = useAPI();
  const navigate = useNavigate();
  const location = useLocation();
  const started = useRef(false);

  useEffect(() => {
    if (active) clearLinkFragment();
  }, [active]);

  useEffect(() => {
    // Wait for the stored session check, so it can't land after (and over) this sign-in
    if (!active || started.current || !apiClient || loading) return;
    started.current = true;
    (async () => {
      let restored = false;
      if (link.handoffId) {
        const found = await apiClient.getFunnelHandoff(link.handoffId);
        if (found.ok && found.data) {
          const query = restoreHandoff(found.data);
          navigate(`${location.pathname}${query}`, { replace: true });
          captureAdClicks();                        // reads the ad click from the address bar, before sign-in tidies it
          restored = true;
        }
        posthog.capture(EVENTS.FUNNEL_HANDOFF_RESTORED, {
          restored, reason: restored ? null : found.code || 'missing', via: link.token ? 'email_link' : 'browser_menu',
          has_character: restored && !!found.data?.image,
        });
      }
      if (link.token) {
        posthog.capture(EVENTS.FUNNEL_EMAIL_LINK_OPENED, { has_handoff: !!link.handoffId, handoff_restored: restored });
        const result = await signInWithEmailLink(link.token);
        if (!result.ok) {
          logger.warn('Email link sign-in failed:', result.code);
          posthog.capture(EVENTS.FUNNEL_SIGNIN_FAILED, { method: 'email_link', reason: result.code || 'unknown' });
          setNotice({ code: result.code || 'unknown', message: result.error || null });
        }
      }
      setDone(true);
    })();
  }, [active, apiClient, loading, link, navigate, location.pathname, signInWithEmailLink]);

  if (!done) {
    // The very loader public/index.html shows while the app downloads (its global classes), with the same
    // wording it switched to for this link: the hand-over from one to the other is invisible
    return (
      <div className="initial-loading" role="status" aria-live="polite">
        <div className="loading-content">
          <img className="loading-video" src="/assets/loading-panda-poster.png" alt="" width="320" height="320" />
          <div className="loading-text">{landingText(link)}</div>
        </div>
      </div>
    );
  }
  return notice ? React.cloneElement(children, { emailLinkNotice: notice }) : children;
};

export default EmailLinkGate;
